import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { Alert, Segmented, Space, Switch, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import {
  GET_AUDIT_LOG,
  GET_IMPERSONATION_SESSIONS,
  GET_USER_ACTIVITY_LOG,
} from "../../../api/graphql/audit";
import {
  ActivityEntry,
  AuditEntry,
  ImpersonationSession,
  actorLabel,
  prettyArgs,
  sessionState,
} from "./activityLog";

const LIMIT = 200;
const when = (value: string) => dayjs(value).format("D MMM YYYY, HH:mm:ss");

const STATE_COLOR = { open: "green", ended: "default", expired: "orange" };

const activityColumns: ColumnsType<ActivityEntry> = [
  { title: "When", dataIndex: "createdAt", width: 190, render: when },
  {
    title: "Who",
    key: "who",
    render: (_, entry) => (
      <Space size={4}>
        {entry.bySupport && <Tag color="purple">Admin</Tag>}
        {actorLabel(entry)}
      </Space>
    ),
  },
  { title: "Type", dataIndex: "entityType", width: 100 },
  { title: "Action", dataIndex: "action" },
];

const ActivitySection: React.FC<{ userId: number }> = ({ userId }) => {
  const [onlyAdmins, setOnlyAdmins] = useState(false);
  const { data, loading, error } = useQuery(GET_USER_ACTIVITY_LOG, {
    variables: { userId, limit: LIMIT, onlyAdmins },
    fetchPolicy: "network-only",
  });

  if (error) return <Alert type="error" message={error.message} />;

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Space>
        <Switch checked={onlyAdmins} onChange={setOnlyAdmins} />
        <Typography.Text>Only what admins did</Typography.Text>
      </Space>
      <Table<ActivityEntry>
        rowKey="id"
        loading={loading}
        dataSource={data?.userActivityLog ?? []}
        columns={activityColumns}
        expandable={{
          rowExpandable: (entry) => entry.changes.length > 0,
          expandedRowRender: (entry) => (
            <ul>
              {entry.changes.map((change, i) => (
                <li key={i}>
                  {change.subject}: {change.detail}
                </li>
              ))}
            </ul>
          ),
        }}
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
      />
    </Space>
  );
};

const auditColumns: ColumnsType<AuditEntry> = [
  { title: "When", dataIndex: "createdAt", width: 190, render: when },
  { title: "Operation", dataIndex: "operation" },
  {
    title: "Result",
    key: "result",
    width: 220,
    render: (_, entry) =>
      entry.success ? (
        <Tag color="green">OK</Tag>
      ) : (
        <Tag color="red">{entry.error || "Failed"}</Tag>
      ),
  },
];

/** Every write the admin made in one session, read when it is opened. */
const SessionWrites: React.FC<{ sessionId: string }> = ({ sessionId }) => {
  const { data, loading, error } = useQuery(GET_AUDIT_LOG, {
    variables: { sessionId, limit: LIMIT },
    fetchPolicy: "network-only",
  });

  if (error) return <Alert type="error" message={error.message} />;

  return (
    <Table<AuditEntry>
      rowKey="id"
      size="small"
      loading={loading}
      dataSource={data?.auditLog ?? []}
      columns={auditColumns}
      expandable={{
        rowExpandable: (entry) => Boolean(entry.args),
        expandedRowRender: (entry) => <pre>{prettyArgs(entry.args)}</pre>,
      }}
      pagination={false}
    />
  );
};

const sessionColumns: ColumnsType<ImpersonationSession> = [
  { title: "Started", dataIndex: "startedAt", width: 190, render: when },
  { title: "Admin", dataIndex: "adminEmail" },
  {
    title: "State",
    key: "state",
    width: 100,
    render: (_, session) => {
      const state = sessionState(session);
      return <Tag color={STATE_COLOR[state]}>{state}</Tag>;
    },
  },
  {
    title: "Ended",
    dataIndex: "endedAt",
    width: 190,
    render: (value) => (value ? when(value) : "—"),
  },
  { title: "IP", dataIndex: "ip", width: 140 },
];

const SessionsSection: React.FC<{ userId: number }> = ({ userId }) => {
  const { data, loading, error } = useQuery(GET_IMPERSONATION_SESSIONS, {
    variables: { userId, limit: LIMIT },
    fetchPolicy: "network-only",
  });

  if (error) return <Alert type="error" message={error.message} />;

  return (
    <Table<ImpersonationSession>
      rowKey="id"
      loading={loading}
      dataSource={data?.impersonationSessions ?? []}
      columns={sessionColumns}
      expandable={{
        expandedRowRender: (session) => (
          <SessionWrites sessionId={session.id} />
        ),
      }}
      pagination={{ defaultPageSize: 20 }}
    />
  );
};

/**
 * Who did what in the user's account. "Activity" is the user's own feed with
 * the admin named on support's lines; "Admin sessions" is each sign-in as the
 * user, opening to every write the admin made during it.
 */
const ActivityLogTab: React.FC<{ userId: number }> = ({ userId }) => {
  const [view, setView] = useState<"activity" | "sessions">("activity");

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Segmented
        value={view}
        onChange={(value) => setView(value as "activity" | "sessions")}
        options={[
          { label: "Activity", value: "activity" },
          { label: "Admin sessions", value: "sessions" },
        ]}
      />
      {view === "activity" ? (
        <ActivitySection userId={userId} />
      ) : (
        <SessionsSection userId={userId} />
      )}
    </Space>
  );
};

export default ActivityLogTab;
